import { z } from 'zod';
import { employmentApi } from '../../../api';
import { EMPLOYMENT_EVENT_TYPES } from '../../../../constants/employmentEventTypes';
import { EVENT_ACTOR_TYPES } from '../../../../constants/employmentActorTypes';
import { SUCCESSOR_EPISODE_PLACEHOLDER } from '../episodeAddress';
import { DEFAULT_SOURCE_SYSTEM, toSourceContractId } from '../../../../constants/employmentSourceSystems';

/**
 * Validation schema for hire
 */
const hireSchema = z.object({
  azureId: z.uuid(),
  email: z.email(),
  fullName: z.string().min(1, 'Full name is required'),
  position: z.uuid(),
  grade: z.uuid().optional(),
  contractType: z.uuid(),
  contractCompany: z.uuid(),
  workingLocation: z.uuid(),
  cluster: z.uuid(),
  hireDate: z.iso.date('Hire date must be in ISO format (YYYY-MM-DD)'),
  // ADR-136: optional contract number. Absent -> the emitted batch is byte-for-byte
  // today's, so open-ended hires are completely unaffected.
  contractNumber: z.string().optional(),
  // ADR-136 phase 4: optional fixed-term capture. Absent -> the emitted batch is
  // byte-for-byte today's [HIRE, HIRE_FINALIZED]. Present -> the batch additionally
  // carries the PRODUCTION resign legs — i.e. exactly the manual post-hire
  // recording flow (ADR-090's FTC signature: singletons + Valid
  // RESIGNATION_SUBMITTED + Pending TERMINATION_FINALIZED), executed inside the
  // same trigger transaction. No backend payload or handler changes, so the
  // radar-ingestion syncs (Americas/Enova) are untouched by construction (§2a.7).
  contractEndDate: z.iso.date('Contract end date must be in ISO format (YYYY-MM-DD)').optional(),
  contractEndReason: z.string().min(1).optional()
}).refine(
  (data) => !data.contractEndDate || data.contractEndDate > data.hireDate,
  { message: 'Contract end date must be after the hire date', path: ['contractEndDate'] }
).refine(
  (data) => !data.contractEndDate || Boolean(data.contractEndReason && data.contractEndReason.trim()),
  { message: 'A fixed-term comment is required when a contract end date is set', path: ['contractEndReason'] }
);

/**
 * Hire - Create New Employee
 *
 * @param {Object} payload - Employee data
 * @param {string} payload.azureId - Required - Azure AD ID
 * @param {string} payload.email - Required
 * @param {string} payload.fullName - Required
 * @param {string} [payload.position] - Required
 * @param {string} [payload.grade] - Optional
 * @param {string} [payload.contractType] - Required
 * @param {string} [payload.contractCompany] - Required
 * @param {string} [payload.workingLocation] - Required
 * @param {string} [payload.cluster] - Required
 * @param {string} payload.hireDate - Required - ISO date format (YYYY-MM-DD)
 * @param {string} [payload.contractNumber] - Optional (ADR-136) - the contract's
 *   number in the system of record. Stamped onto the new employment episode as
 *   ADR-049 `sourceReference` { source: 'radar', contractId }. The source is always
 *   stamped (ADR-160 §5.4); the contractId only when a number was entered.
 * @param {string} [actorType='User'] - Actor type (System, User, Import)
 * @returns {Promise<{employeeId: string}>} - Returns employeeId for subsequent events
 * @throws {z.ZodError} - If validation fails
 */
export const hire = async (payload, actorType = EVENT_ACTOR_TYPES.USER) => {

  // Validate payload
  const validatedPayload = hireSchema.parse(payload);

  // ADR-136: the contract number rides the hire.finalized payload as ADR-049's
  // sourceReference — the backend already accepts it and persists it onto the
  // episode, so no API change is involved. Blank -> the key is omitted entirely.
  const contractId = toSourceContractId(validatedPayload.contractNumber);
  // ADR-160 §5.4 — the stamp is unconditional: a radar contract without a
  // number used to carry no sourceReference at all, which radar-api reads as
  // radar-owned but the Americas sync reads as ADOPTABLE by start date. The
  // contract number joins the stamp when there is one.
  const sourceReference = contractId === null
    ? { source: DEFAULT_SOURCE_SYSTEM }
    : { source: DEFAULT_SOURCE_SYSTEM, contractId };

  // ADR-136 phase 4 (A2): one sentence, two destinations — the episode comment
  // (via hire.finalized.comments, replacing the default text) and the
  // resignation reason on the scheduled end.
  const fixedTermReason = validatedPayload.contractEndDate
    ? validatedPayload.contractEndReason.trim()
    : null;

  const events = [{
    type: EMPLOYMENT_EVENT_TYPES.HIRE,
    payload: {
      azureId: validatedPayload.azureId,
      email: validatedPayload.email,
      fullName: validatedPayload.fullName,
      positionId: validatedPayload.position,
      gradeId: validatedPayload.grade,
      contractTypeId: validatedPayload.contractType,
      contractCompanyId: validatedPayload.contractCompany,
      workingLocationId: validatedPayload.workingLocation,
      clusterId: validatedPayload.cluster
    }
  }, {
    type: EMPLOYMENT_EVENT_TYPES.HIRE_FINALIZED,
    payload: {
      employeeId: "${employeeId}",
      hireDate: validatedPayload.hireDate,
      ...(sourceReference ? { sourceReference } : {}),
      ...(fixedTermReason ? { comments: fixedTermReason } : {})
    }
  }];

  if (validatedPayload.contractEndDate) {
    // ADR-136 phase 4: the scheduled end rides the SAME production resign legs the
    // manual post-hire flow uses. resign.submitted writes the singletons (§6 #1:
    // a fixed-term end IS a resignation) and records the Valid
    // RESIGNATION_SUBMITTED; resign.finalized leaves the Pending
    // TERMINATION_FINALIZED that is the FTC report's population signature
    // (ADR-090). The ${employeeId} placeholder resolves from the trigger batch
    // context, and the whole batch is one transaction — no partial FTC states.
    events.push({
      type: EMPLOYMENT_EVENT_TYPES.RESIGNATION_SUBMITTED,
      payload: {
        employeeId: "${employeeId}",
        // ADR-155 §5 — the contract HIRE_FINALIZED just created
        employmentEpisodeId: SUCCESSOR_EPISODE_PLACEHOLDER,
        resignationDate: validatedPayload.contractEndDate,
        resignationReason: fixedTermReason
      }
    }, {
      type: EMPLOYMENT_EVENT_TYPES.TERMINATION_FINALIZED,
      payload: {
        employeeId: "${employeeId}",
        employmentEpisodeId: SUCCESSOR_EPISODE_PLACEHOLDER
      }
    });
  }

  return await employmentApi.triggerEmploymentEvents({
    metadata: {
      actorType
    },
    events
  });
};
